// This component uses the MSAL instance provided by the MsalProvider at the app root.
// Do NOT create a new PublicClientApplication instance here. Use the useMsal() hook as shown below.

import React, { useEffect, useState, useRef } from 'react';
import { getDataSources, createDataSource, updateDataSource } from '../Services/DataSourceService';
import { useMsal } from '@azure/msal-react';
import './DataSourceConfig.css';

const DataSourceConfig: React.FC = () => {
  const [dataSources, setDataSources] = useState<any[]>([]);
  const [originalDataSources, setOriginalDataSources] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [duplicateCompanies, setDuplicateCompanies] = useState<Set<string>>(new Set());
  const [duplicateUrls, setDuplicateUrls] = useState<Set<string>>(new Set());
  const [invalidUrls, setInvalidUrls] = useState<Set<string>>(new Set());
  const { instance, accounts } = useMsal();
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getDataSources(instance, accounts[0]);
        const arr = Array.isArray(data) ? data : [data];
        setDataSources(arr);
        setOriginalDataSources(arr);
      } catch (err: any) {
        setError('Failed to load data sources');
      } finally {
        setLoading(false);
      }
    };
    if (accounts && accounts.length > 0) {
      fetchData();
    }
  }, [accounts.length, instance]);

  // Handler stubs for add/remove (implement as needed)
  const handleAdd = () => {
    setDataSources([
      ...dataSources,
      { company: '', dataSourcePath: '' }
    ]);
  };
  const handleRemove = (idx: number) => {
    // TODO: Implement remove logic
  };

  const validateCompanyUniqueness = () => {
    const companyNames = dataSources.map(ds => ds.company.toLowerCase().trim()).filter(name => name);
    const duplicates = new Set<string>();
    const seen = new Set<string>();
    
    companyNames.forEach(name => {
      if (seen.has(name)) {
        duplicates.add(name);
      } else {
        seen.add(name);
      }
    });
    
    setDuplicateCompanies(duplicates);
    
    if (duplicates.size > 0) {
      setValidationError('Company names must be unique.');
    } else {
      setValidationError(null);
    }
  };

  const validateUrlUniqueness = () => {
    const urls = dataSources.map(ds => ds.dataSourcePath.toLowerCase().trim()).filter(url => url);
    const duplicates = new Set<string>();
    const seen = new Set<string>();
    
    urls.forEach(url => {
      if (seen.has(url)) {
        duplicates.add(url);
      } else {
        seen.add(url);
      }
    });
    
    setDuplicateUrls(duplicates);
    
    if (duplicates.size > 0) {
      setValidationError('Sharepoint Location (VDR) URLs must be unique.');
    } else {
      setValidationError(null);
    }
  };

  const isValidUrl = (urlString: string): boolean => {
    try {
      const url = new URL(urlString);
      // Check if it's a valid SharePoint URL
      return url.protocol === 'https:' && 
             (url.hostname.includes('sharepoint.com') || 
              url.hostname.includes('.sharepoint.') ||
              url.hostname.includes('onedrive.'));
    } catch {
      return false;
    }
  };

  const validateUrlFormat = () => {
    const invalidUrlSet = new Set<string>();
    
    dataSources.forEach(ds => {
      if (ds.dataSourcePath && ds.dataSourcePath.trim() && !isValidUrl(ds.dataSourcePath.trim())) {
        invalidUrlSet.add(ds.dataSourcePath.toLowerCase().trim());
      }
    });
    
    setInvalidUrls(invalidUrlSet);
    
    if (invalidUrlSet.size > 0) {
      setValidationError('Please enter valid HTTPS URLs for SharePoint locations.');
    }
  };

  const validateUniqueness = () => {
    const companyNames = dataSources.map(ds => ds.company.toLowerCase().trim()).filter(name => name);
    const urls = dataSources.map(ds => ds.dataSourcePath.toLowerCase().trim()).filter(url => url);
    
    // Check for duplicate companies
    const companyDuplicates = new Set<string>();
    const seenCompanies = new Set<string>();
    companyNames.forEach(name => {
      if (seenCompanies.has(name)) {
        companyDuplicates.add(name);
      } else {
        seenCompanies.add(name);
      }
    });
    
    // Check for duplicate URLs
    const urlDuplicates = new Set<string>();
    const seenUrls = new Set<string>();
    urls.forEach(url => {
      if (seenUrls.has(url)) {
        urlDuplicates.add(url);
      } else {
        seenUrls.add(url);
      }
    });
    
    setDuplicateCompanies(companyDuplicates);
    setDuplicateUrls(urlDuplicates);
    
    // Also validate URL format
    const invalidUrlSet = new Set<string>();
    dataSources.forEach(ds => {
      if (ds.dataSourcePath && ds.dataSourcePath.trim() && !isValidUrl(ds.dataSourcePath.trim())) {
        invalidUrlSet.add(ds.dataSourcePath.toLowerCase().trim());
      }
    });
    setInvalidUrls(invalidUrlSet);
    
    // Check for various validation errors
    if (companyDuplicates.size > 0 && urlDuplicates.size > 0) {
      setValidationError('Company names and Sharepoint Location (VDR) URLs must be unique.');
    } else if (companyDuplicates.size > 0) {
      setValidationError('Company names must be unique.');
    } else if (urlDuplicates.size > 0) {
      setValidationError('Sharepoint Location (VDR) URLs must be unique.');
    } else if (invalidUrlSet.size > 0) {
      setValidationError('Please enter valid HTTPS URLs for SharePoint locations.');
    } else {
      setValidationError(null);
    }
  };

  const handleCompanyChange = (idx: number, value: string) => {
    const newData = [...dataSources];
    newData[idx] = { ...newData[idx], company: value };
    setDataSources(newData);
  };

  const handleUrlChange = (idx: number, value: string) => {
    const newData = [...dataSources];
    newData[idx] = { ...newData[idx], dataSourcePath: value };
    setDataSources(newData);
  };

  const handleCompanyBlur = () => {
    validateUniqueness();
  };

  const handleUrlBlur = () => {
    validateUniqueness();
  };

  const handleSave = async () => {
    setValidationError(null);
    setIsSaving(true);
    // Validation: all company and dataSourcePath must be non-empty
    for (const ds of dataSources) {
      if (!ds.company || !ds.dataSourcePath) {
        setValidationError('Company and Sharepoint Location (VDR) cannot be empty.');
        setIsSaving(false);
        return;
      }
    }
    
    // Validation: company names must be unique
    const companyNames = dataSources.map(ds => ds.company.toLowerCase().trim());
    const uniqueCompanyNames = new Set(companyNames);
    if (companyNames.length !== uniqueCompanyNames.size) {
      setValidationError('Company names must be unique.');
      setIsSaving(false);
      return;
    }
    
    // Validation: URLs must be unique
    const urls = dataSources.map(ds => ds.dataSourcePath.toLowerCase().trim());
    const uniqueUrls = new Set(urls);
    if (urls.length !== uniqueUrls.size) {
      setValidationError('Sharepoint Location (VDR) URLs must be unique.');
      setIsSaving(false);
      return;
    }
    
    // Validation: URLs must be valid format
    for (const ds of dataSources) {
      if (ds.dataSourcePath && !isValidUrl(ds.dataSourcePath.trim())) {
        setValidationError('Please enter valid HTTPS URLs for SharePoint locations.');
        setIsSaving(false);
        return;
      }
    }
    
    // Clear validation error if we reach here
    setValidationError(null);
    try {
      const updatedDataSources = await Promise.all(
        dataSources.map(async (ds) => {
          if (ds.id) {
            await updateDataSource(instance, ds.id, { ...ds, DataSourceType: 'Sharepoint' }, accounts[0]);
            return ds;
          } else {
            const created = await createDataSource(instance, { ...ds, DataSourceType: 'Sharepoint' }, accounts[0]);
            return { ...ds, id: created.id };
          }
        })
      );
      setDataSources(updatedDataSources);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save data sources');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="dsconfig-container">
      {isSaving && (
        <div className="spinner-overlay">
          <div className="spinner spinner-large" />
        </div>
      )}
      {showSuccess && (
        <div className="success-popup">
          Data sources saved successfully!
        </div>
      )}
      <h2 className="dsconfig-header">
        Sharepoint (VDR) location configuration:
      </h2>
      <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
        Enter valid HTTPS URLs for SharePoint locations (e.g., https://company.sharepoint.com/sites/sitename)
      </p>
      <div className="dsconfig-table-header">
        <div className="dsconfig-table-header-row">
          <div className="dsconfig-col-company">Company Name</div>
          <div className="dsconfig-col-path">
            <span>Sharepoint Location (VDR)</span>
          </div>
        </div>
        {/* Removed the plus icon button here */}
      </div>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {!loading && !error && (
        <div>
          {dataSources.map((ds, idx) => (
            <div
              key={ds.id || idx}
              className="dsconfig-table-row"
            >
              <div className="dsconfig-col-company">
                <input
                  type="text"
                  value={ds.company}
                  onChange={e => handleCompanyChange(idx, e.target.value)}
                  onBlur={handleCompanyBlur}
                  className={`dsconfig-input dsconfig-input-company ${
                    duplicateCompanies.has(ds.company.toLowerCase().trim()) ? 'error' : ''
                  }`}
                  style={{
                    borderColor: duplicateCompanies.has(ds.company.toLowerCase().trim()) ? 'red' : undefined,
                    backgroundColor: duplicateCompanies.has(ds.company.toLowerCase().trim()) ? '#ffebee' : undefined
                  }}
                />
              </div>
              <div className="dsconfig-col-path">
                <input
                  type="text"
                  value={ds.dataSourcePath}
                  onChange={e => handleUrlChange(idx, e.target.value)}
                  onBlur={handleUrlBlur}
                  className={`dsconfig-input dsconfig-input-path ${
                    duplicateUrls.has(ds.dataSourcePath.toLowerCase().trim()) || 
                    invalidUrls.has(ds.dataSourcePath.toLowerCase().trim()) ? 'error' : ''
                  }`}
                  style={{
                    borderColor: duplicateUrls.has(ds.dataSourcePath.toLowerCase().trim()) || 
                                invalidUrls.has(ds.dataSourcePath.toLowerCase().trim()) ? 'red' : undefined,
                    backgroundColor: duplicateUrls.has(ds.dataSourcePath.toLowerCase().trim()) || 
                                    invalidUrls.has(ds.dataSourcePath.toLowerCase().trim()) ? '#ffebee' : undefined
                  }}
                  placeholder="https://company.sharepoint.com/sites/sitename"
                />
              </div>
              <button
                className="dsconfig-remove-btn"
                title="Remove"
                onClick={() => handleRemove(idx)}
              >
                &minus;
              </button>
            </div>
          ))}
          {validationError && <div style={{ color: 'red', marginBottom: 8 }}>{validationError}</div>}
          <div className="dsconfig-action-row">
            <button
              className="dsconfig-add-btn"
              onClick={handleAdd}
              disabled={isSaving}
            >
              Add Data Source
            </button>
            <button
              className="dsconfig-save-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSourceConfig;
